import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { companyInfo } from '../mockData';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Store in localStorage for now (mock functionality)
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({
      ...formData,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));

    toast({
      title: "Message Sent Successfully!",
      description: "We'll get back to you within 24 hours.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Contact Arikone - Get in Touch | Digital Marketing Agency Noida"
        description="Contact Arikone for digital marketing services in Noida. Visit us at Jagriti Palace, Sector 18, Noida or call +91 96955 35755. Free consultation available for brand promotion and marketing services."
        keywords="contact arikone, digital marketing agency contact, noida marketing agency, brand promotion enquiry, marketing consultation noida"
      />

      {/* Hero Section (Split Depth Design) */}
      <section className="bg-blue-900 pt-32 pb-48 relative overflow-hidden">
        {/* Background Patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500 rounded-full blur-3xl mix-blend-multiply filter opacity-50 animate-blob"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500 rounded-full blur-3xl mix-blend-multiply filter opacity-50 animate-blob animation-delay-2000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-5xl sm:text-7xl font-bold text-white mb-6 animate-fadeInUp tracking-tight">
            Let's Start a <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-200">
              Conversation
            </span>
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto animate-fadeInUp stagger-1 leading-relaxed">
            Have a project in mind? We'd love to hear about it. Let's discuss how we can help your business grow.
          </p>
        </div>
      </section>

      {/* Contact Info Cards (Overlapping) */}
      <section className="pb-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-24 relative z-20">
            {[
              {
                icon: MapPin,
                title: 'Visit Us',
                content: companyInfo.address,
                color: 'blue'
              },
              {
                icon: Phone,
                title: 'Call Us',
                content: companyInfo.phone,
                color: 'emerald'
              },
              {
                icon: Mail,
                title: 'Email Us',
                content: companyInfo.email,
                color: 'purple'
              },
              {
                icon: Clock,
                title: 'Working Hours',
                content: companyInfo.hours,
                color: 'orange'
              }
            ].map((item, index) => (
              <Card key={index} className={`hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-0 shadow-lg animate-fadeInUp stagger-${index + 1}`}>
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 bg-${item.color}-50 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <item.icon className={`text-${item.color}-600`} size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="animate-fadeInUp">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <Card className="hover-glow">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="Your company"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service">Service Interested In</Label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600"
                      >
                        <option value="">Select a service</option>
                        <option value="Digital Marketing">Digital Marketing</option>
                        <option value="Social Media Marketing">Social Media Marketing</option>
                        <option value="Performance Ads">Performance Ads</option>
                        <option value="Website Development">Website Development</option>
                        <option value="Branding & Design">Branding & Design</option>
                        <option value="Lead Generation">Lead Generation</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="mt-1 min-h-[120px]"
                        placeholder="Tell us about your project..."
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white btn-primary"
                      size="lg"
                    >
                      Send Message <Send className="ml-2" size={18} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Map Section */}
            <div className="animate-fadeInUp stagger-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Find Us on Map</h2>
              <Card className="h-[600px] overflow-hidden hover-glow">
                <CardContent className="p-0 h-full">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.9116149620195!2d77.32214997555072!3d28.57241667569759!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x28f8eb89b17a90cb%3A0xca3aa130bb1e5e9e!2sArikone!5e0!3m2!1sen!2sin!4v1769929428674!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Arikone Location"
                  ></iframe>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-yellow-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl mb-8 text-blue-50">
            Book a free consultation call with our experts today
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg btn-primary"
          >
            <a href={`tel:${companyInfo.phone}`}>Call {companyInfo.phone}</a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;
